'use client';

import { cx } from 'class-variance-authority';
import { motion, type Variants } from 'motion/react';
import Portal from './Portal';
import useBlockBodyScrolling from './hooks/useBlockBodyScrolling';
import ModalBackground from './Modal/ModalBackground';
import { useEventListener } from './hooks/DOM';
import { cn } from '~/utils';

const DEFAULT_VARIANTS: Variants = {
    initial: { y: '100%' },
    animate: { y: 0 },
    exit: { y: '100%' },
};

const SearchModal: React.FC<{
    className?: string;
    modalClassName?: string;
    backgroundClassName?: string;
    variants?: Variants;
    onClose: () => void;
    children: React.ReactNode;
}> = props => {
    useEventListener('window', 'keydown', e => {
        if (e.key === 'Escape') props.onClose?.();
    });
    useBlockBodyScrolling();

    const variants = props.variants ?? DEFAULT_VARIANTS;

    return (
        <Portal querySelector="#modals">
            <ModalBackground onClick={props.onClose} className={cn(props.backgroundClassName)} />
            <motion.dialog
                open
                data-testid="SearchModal"
                variants={variants}
                initial="initial"
                animate="animate"
                exit="exit"
                aria-modal="true"
                tabIndex={-1}
                className={cn(
                    // Undo the user-agent dialog box (fit-content size, inline insets, canvas colours).
                    'h-auto max-h-none max-w-none bg-transparent text-inherit',
                    'pointer-events-none fixed bottom-0 z-modals w-full overflow-y-auto sm:inset-0',
                    props.modalClassName,
                )}
            >
                <div
                    className={cx(
                        'pointer-events-auto relative mx-auto my-0 h-full rounded-t-xl sm:my-16 sm:rounded-xl',
                        props.className,
                    )}
                >
                    {props.children}
                </div>
            </motion.dialog>
        </Portal>
    );
};

export default SearchModal;
